import React, { useEffect, useRef, useState } from 'react';

/**
 * True where there is no pointer to hover with. The invite page's main audience
 * reads it on a phone, so the pan cannot be hover-only — without this the whole
 * preview is a static crop and the hint asks for a gesture that does not exist.
 */
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(hover: none)');
    const sync = (event: MediaQueryListEvent) => setCoarse(event.matches);
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  return coarse;
}

interface ScrollPreviewImageProps {
  src: string;
  alt: string;
  /**
   * Fixed height of the visible window, in px. Omit to let the parent control
   * sizing (e.g. an aspect-ratio box) — pass `className="h-full w-full"` then.
   */
  height?: number;
  className?: string;
  imgClassName?: string;
  /** Show the "Hover to scroll" hint pill. Default true. */
  hint?: boolean;
  onError?: () => void;
}

/**
 * A fixed-height "window" onto a tall (full-page) screenshot. The image pans
 * down at a steady speed to reveal the rest of the page, then slides back to the
 * top. Pure CSS transform — no scrollbar, no layout shift. Falls back to a
 * static top-aligned image when the shot isn't taller than the window (offset
 * stays 0).
 *
 * Hover drives it where there is a pointer; on touch it is a tap that toggles,
 * because the pan is the whole point of the component and a phone would
 * otherwise see one static crop.
 */
const ScrollPreviewImage: React.FC<ScrollPreviewImageProps> = ({
  src,
  alt,
  height,
  className = '',
  imgClassName = '',
  hint = true,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [offset, setOffset] = useState(0);
  const [panning, setPanning] = useState(false);
  const coarse = useCoarsePointer();

  const start = () => {
    setPanning(true);
    const img = imgRef.current;
    const box = containerRef.current;
    if (!img || !box) return;
    const overflow = img.clientHeight - box.clientHeight;
    setOffset(overflow > 0 ? overflow : 0);
  };

  const stop = () => {
    setPanning(false);
    setOffset(0);
  };

  const handleEnter = () => {
    if (coarse) return;
    start();
  };

  const handleLeave = () => {
    if (coarse) return;
    stop();
  };

  const handleTap = () => {
    if (!coarse) return;
    if (panning) stop();
    else start();
  };

  // Pan at a steady ~80px/sec so short and tall pages feel consistent.
  const durationMs = Math.max(1200, Math.round((offset / 80) * 1000));

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={height != null ? { height } : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleTap}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        onError={onError}
        className={`block w-full h-auto ${imgClassName}`}
        style={{
          transform: `translateY(-${offset}px)`,
          transition: `transform ${durationMs}ms linear`,
        }}
      />
      {hint && (
        <div
          className={`pointer-events-none absolute bottom-3 right-3 transition-opacity duration-300 ${
            panning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m0 0l-4-4m4 4l4-4M12 4L8 8m4-4l4 4" />
            </svg>
            {coarse ? 'Tap to scroll' : 'Hover to scroll'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ScrollPreviewImage;
